import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import { Button, Modal, useMediaQuery } from '@mui/material';
import Select, { components } from 'react-select';
import React, { Fragment, useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';

import styles from './styles.module.css';
import { useEffect } from 'react';

// *@Styles for Component */
const selectStylesConfig = {
  control: (provided) => ({
    ...provided,
    border: 'none',
    margin: '4px 7px',
    fontFamily: 'Roboto',
    fontSize: '12px',
    lineHeight: '1.2',
    color: '#98A0A6',
    boxShadow: 'none',
    backgroundColor: '#fff',
    display: 'none',
  }),
  placeholder: (defaultStyles) => ({
    ...defaultStyles,
    fontFamily: 'Roboto',
    fontSize: '12px',
    lineHeight: '1.2',
    color: '#98A0A6',
    backgroundColor: '#fff',
  }),
  menu: () => ({
    backgroundColor: '#fff',
  }),
  menuList: (defaultStyles) => ({
    ...defaultStyles,
    display: 'flex',
    flexWrap: 'wrap',
  }),
};

// *@Styles for options */
const optionStylesConfig = {
  backgroundColor: '#fff',
  alignItems: 'center',
  gap: '0 5px',
  padding: '5px 16px',
  display: 'flex ',
  fontFamily: "Roboto', 'Helvetica', 'Arial', sans-serif",
  fontWeight: 400,
  fontSize: '13px',
  lineHeight: 1.4,
  color: '#31414E',
  cursor: 'pointer',
  width: '25%',
};

// *@Main Component */
const MultiDropdown = ({
  value,
  onChange,
  onDropdownOpen,
  placeholderSearch,
  placeholder,
  options,
  isSingle,
  style,
}) => {
  // States
  const [openPopupState, setOpenPopupState] = useState(false);
  const [openState, setOpenState] = useState(false);
  const multiOptions = [...options];
  const countOfOption = 2;

  // Vars
  const isRenderMoreButton = value && value.length > countOfOption;
  const valuesLength = value.length;

  // Handlers
  const handleOpenState = () => {
    onDropdownOpen();
    setOpenState((prevState) => !prevState);
  };

  const handleChange = (newValue) => {
    onChange(newValue);
  };

  const handlePopupState = () => {
    setOpenPopupState((prevState) => !prevState);
  };

  const handleRemoveValue =
    (itemValue, isChangeState = false) =>
    (_event) => {
      const oldValuesState = [...value];
      const newValuesState = oldValuesState.filter(
        (item) => item.value !== itemValue.value,
      );
      onChange(newValuesState);

      isChangeState && handleOpenState();
    };

  const handleMore = () => {
    handlePopupState();
    handleOpenState();
  };

  useEffect(() => {
    if (value.length === 0) setOpenPopupState(false);
  }, [value]);

  const renderOptionPopup =
    value && value.length > 0 ? (
      <Modal open={openPopupState} onClose={handlePopupState}>
        <div className={styles.optionPopup}>
          {value?.map((item) => (
            <div className={styles.inputValue}>
              {item.label}
              <CloseIcon
                sx={{
                  width: '15px',
                  height: '15px',
                  marginLeft: 'auto',
                  cursor: 'pointer',
                }}
                onClick={handleRemoveValue(item)}
              />
            </div>
          ))}
        </div>
      </Modal>
    ) : null;

  // Renders
  const renderDropIcon = openState ? (
    <KeyboardArrowUpOutlinedIcon className={styles.arrowDownIcon} />
  ) : (
    <KeyboardArrowDownOutlinedIcon className={styles.arrowDownIcon} />
  );

  const renderMoreButton = isRenderMoreButton ? (
    <button onClick={handleMore} className={styles.moreButton}>
      +{valuesLength - countOfOption} more
    </button>
  ) : null;

  const renderInputOptions =
    value &&
    value?.map((item, index) =>
      index < countOfOption ? (
        <div className={styles.inputValue}>
          {item.label}
          <CloseIcon
            sx={{ width: '15px', height: '15px' }}
            onClick={handleRemoveValue(item, true)}
          />
        </div>
      ) : null,
    );

  const renderInputContent =
    value && value.length > 0 ? (
      <Fragment>
        {renderInputOptions}
        {renderMoreButton}
      </Fragment>
    ) : (
      <div>{placeholder}</div>
    );

  const renderTarget = (
    <Button
      className={styles.dropdownRoot}
      onClick={handleOpenState}
      endIcon={renderDropIcon}
      disableTouchRipple
      disableFocusRipple
      disableRipple
    >
      {renderInputContent}
    </Button>
  );

  return (
    <Fragment>
      <DropdownRoot
        open={openState}
        onClose={handleOpenState}
        target={renderTarget}
        isSingle={isSingle}
        style={style}
      >
        <Select
          value={value}
          options={multiOptions}
          placeholder={placeholderSearch}
          onChange={handleChange}
          styles={selectStylesConfig}
          backspaceRemovesValue={false}
          controlShouldRenderValue={false}
          hideSelectedOptions={true}
          isClearable={false}
          menuIsOpen
          isMulti
          tabSelectsValue={false}
          components={{
            DropdownIndicator,
            IndicatorSeparator: null,
            Option: (props) => InputOption({ ...props, isSingle }),
          }}
        />
      </DropdownRoot>
      {renderOptionPopup}
    </Fragment>
  );
};

// *@Dropdown Button for select */
function DropdownRoot({ children, open, isSingle, target, onClose, style }) {
  return (
    <div style={{ position: 'relative', ...style }}>
      {target}
      {open ? <Paper isSingle={isSingle}>{children}</Paper> : null}
      {open ? <Blanket onClick={onClose} /> : null}
    </div>
  );
}

// *@Component for outside click */
function Blanket(props) {
  return <div className={styles.blanketRoot} {...props} />;
}

// *@Paper menu for select */
function Paper(props) {
  const laptopHeightQuery = useMediaQuery('(max-height: 1200px)');
  const style = props.isSingle && {
    width: '100%',
    bottom: laptopHeightQuery ? '60px' : 'unset',
  };
  return <div className={styles.paper} style={style} {...props} />;
}

// *@Search Icon */
function DropdownIndicator() {
  return (
    <div className={styles.dropdownIndicatorRoot}>
      <Svg>
        <path
          d="M16.436 15.085l3.94 4.01a1 1 0 0 1-1.425 1.402l-3.938-4.006a7.5 7.5 0 1 1 1.423-1.406zM10.5 16a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11z"
          fill="currentColor"
          fillRule="evenodd"
        />
      </Svg>
    </div>
  );
}

// *@Svg Component */
function Svg(p) {
  return (
    <svg
      width="18"
      height="20"
      viewBox="0 0 24 24"
      focusable="false"
      role="presentation"
      {...p}
    />
  );
}

// *@Dropdown Menu's options */
function InputOption({
  getStyles,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  isSingle,
  value,
  ...rest
}) {
  const styleOpt = { ...optionStylesConfig, width: '100%' };

  // prop assignment
  const props = {
    ...innerProps,
    style: styleOpt,
  };

  return (
    <Fragment>
      <components.Option
        isDisabled={isDisabled}
        isFocused={isFocused}
        isSelected={isSelected}
        getStyles={getStyles}
        innerProps={props}
        {...rest}
      >
        <div>{children}</div>
      </components.Option>
    </Fragment>
  );
}

export default MultiDropdown;
